import styled, { css } from 'styled-components'
import media from 'css-in-js-media'

import React, { useRef, useState, useCallback, useLayoutEffect, useEffect, useContext } from 'react'
import ResizeObserver from 'resize-observer-polyfill'
import { useViewportScroll, useTransform, useSpring, motion, useAnimation } from 'framer-motion'

import { FormattedMessage } from 'react-intl'

import Button from './Button'
import Container from './Container'
import SelectLanguage, { InnerWrapper, LanguageSelected, LanguageSwitcher } from './SelectLanguage'
import LayoutContext from './LayoutContext'
import LocalLink from './LocalLink'

import Logo from '../svg/logo.svg'
import { Variables } from '../styles/Variables'
import Navigation, { StyledNavigation } from './Navigation'
import useCurrentWidth from './windowSize'
import { Button_StylePrimary, StyledButton } from './ButtonStyled'
import { useHeaderData } from '../hooks/HeaderData'
import { Row } from 'react-styled-flexboxgrid'
import Close from '../svg/close.svg'

const menuBreakpoint = 1170

export const LogoWrap = styled.div`
    height: 40px;
    transition: 0.2s opacity ease-out;
    text-align: left;
    img,
    svg {
        height: 100%;
        width: auto;

        path {
            stroke: ${Variables.color.white};
            fill: ${Variables.color.white};
        }
    }
`

const HeaderWrapper = styled.div`
    display: flex;
    justify-content: flex-start;
    align-items: center;
    width: 100%;
    box-sizing: border-box;
    padding-top: 30px;
    padding-bottom: 30px;
    font-family: ${Variables.font.body};
    @media (max-width: ${menuBreakpoint - 1}px) {
        justify-content: space-between;
    }

    ${StyledButton} {
        max-width: 100%;
        min-width: 100%;
    }
`

const DesktopMenu = styled.div`
    display: inherit;
    justify-content: inherit;
    align-items: inherit;
    flex-grow: 1;
    @media (max-width: ${menuBreakpoint - 1}px) {
        display: none;
    }
`

const BrochureButton = styled.div`
    margin-left: 20px;
`

export const MenuToggle = styled.div`
    position: relative;
    width: 28px;
    height: 30px;
    cursor: pointer;
    transition: 0.2s opacity ease-out;
    border-top: 3px solid ${Variables.color.white};

    @media (min-width: ${menuBreakpoint}px) {
        display: none;
    }

    ${(props) =>
        props.isMenuOpen &&
        css`
            @media (max-width: ${menuBreakpoint - 1}px) {
                display: none;
            }
        `};

    :before,
    :after {
        content: '';
        height: 3px;
        background: ${Variables.color.white};
        position: absolute;
        left: 0;
        right: 0;
        transition: 0.2s transform ease-out;
        will-change: transform;
    }

    :before {
        top: 8px;
        transform-origin: 18px 7px;
    }

    :after {
        top: 19px;
        transform-origin: 15px -4px;
    }

    ${(props) =>
        props.isMenuOpen &&
        css`
            :before,
            :after {
                height: 1px;
                width: 24px;
            }
            :before {
                transform: rotateZ(-45deg);
            }
            :after {
                transform: rotateZ(45deg);
            }
        `}
`

const MobileClose = styled.span`
    font-family: ${Variables.font.title};
    font-size: 12px;
    line-height: 14px;
    text-align: center;
    text-transform: uppercase;
    color: ${Variables.color.white};
    display: none;

    ${(props) =>
        props.isMenuOpen &&
        css`
            @media (max-width: ${menuBreakpoint - 1}px) {
                display: block;
            }
        `}
`

const SidebarNavigation = styled(motion.div)`
    position: fixed;
    width: 100%;
    bottom: 0;
    top: 0;
    left: 0;
    pointer-events: none;
    z-index: 10;
    background-color: ${Variables.color.pazola};
    opacity: 0;

    ${(props) =>
        props.isActive &&
        css`
            pointer-events: all;
        `}
`

const SidebarNavigationInner = styled.div`
    padding: 250px 30px 40px;
    color: ${Variables.color.white};
    height: 100%;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;

    @media (min-width: ${menuBreakpoint}px) {
        padding-left: 80px;
        padding-right: 80px;
    }

    @media (max-width: ${menuBreakpoint - 1}px) {
        padding-left: 0;
    }
`

const MobileNavigation = styled.div`
    flex-grow: 1;
    ${StyledNavigation} {
        display: flex;
        flex-direction: column;
        margin: 0;
    }

    a {
        font-family: ${Variables.font.body};
        font-weight: normal;
        font-size: 18px;
        line-height: 120%;
        letter-spacing: 0.0015em;
        color: ${Variables.color.white};
        margin-left: 0;
        margin-bottom: 28px;
    }
`

const Colours = css`
    transition: 0.5s background-color ease-out;

    a {
        transition: 0.3s color ease-out;
    }

    ${LanguageSwitcher} {
        transition: 0.3s color ease-out;
    }

    path {
        transition: 0.3s fill ease-out, 0.3s stroke ease-out;
    }
`

const invertedColours = css`
    background: ${Variables.color.pazolaKarry};
    ${StyledNavigation} a {
        color: ${Variables.color.pazola};
        &:hover {
            border-color: ${Variables.color.pazola};
        }
        &.active {
            border-color: ${Variables.color.pazola};
        }
    }

    ${LogoWrap} path {
        fill: ${Variables.color.pazola};
        stroke: ${Variables.color.pazola};
        @media (max-width: ${menuBreakpoint - 1}px) {
            stroke: ${Variables.color.white};
            fill: ${Variables.color.white};
        }
    }

    ${LanguageSwitcher} {
        color: ${Variables.color.pazola};

        a {
            color: ${Variables.color.pazola};
        }

        path {
            stroke: ${Variables.color.pazola};
        }
    }
    ${StyledButton} {        
        ${Button_StylePrimary}
    }

    ${MenuToggle} {
        :before,
        :after {
            color: ${Variables.color.pazola};
        }
    }

    ${LanguageSelected} {
        color: ${Variables.color.pazola};
    }
`

export const StickyHeader = styled(motion.div)`
    position: fixed;
    width: 100%;
    top: 0;
    left: 0;
    z-index: 100;
    ${(props) =>
        !props.isStuck &&
        css`
            background: linear-gradient(180deg, rgba(0, 0, 0, 0.3) 0%, rgba(0, 0, 0, 0) 100%);
        `}

    @media (max-width: ${menuBreakpoint - 1}px) {
        background: transparent;
    }

    ${(props) =>
        props.isLanguageMenuOpen &&
        css`
            pointer-events: none;
            ${LogoWrap} {
                opacity: 0;
            }
            ${MenuToggle} {
                pointer-events: all;
            }
        `}
    ${(props) =>
        props.SidebarOpen &&
        css`
            pointer-events: none;
            ${MenuToggle} {
                opacity: 0;
            }
        `}
    ${(props) =>
        props.noticeBar &&
        css`
            @media (max-width: ${menuBreakpoint - 1}px) {
                top: 49px;
            }
        `}
    
    ${Colours}
    ${(props) =>
        props.isStuck && !props.SidebarOpen && !props.isLanguageMenuOpen && !props.isMenuOpen && invertedColours}
    :hover {
        @media (min-width: ${menuBreakpoint}px) {
            ${(props) => !props.SidebarOpen && !props.isMenuOpen && invertedColours}
        }
        @media (min-width: ${menuBreakpoint}px) {
            ${invertedColours}
        }
    }

    @media (max-width: 1290px) {
        ${Container} {
            padding-left: 20px;
            padding-right: 20px;
        }
    }
    @media (max-width: 1200px) {
        ${StyledNavigation} {
            margin-left: 20px;
        }
    }
`

const TopBar = styled(Row)`
    background-color: ${Variables.color.pazola};
    padding: 12px 20px;
    margin: 0;

    > .navigation-open {
        display: none;
    }
`

const StyledMobileView = styled.div`
    @media (min-width: ${menuBreakpoint}px) {
        display: none;
    }

    ${StyledButton} {
        color: ${Variables.color.white};
        border-color: ${Variables.color.white};
        background: transparent;
        &::before {
            border-color: ${Variables.color.white};
        }
    }
    ${InnerWrapper} {
        svg path {
            stroke: ${Variables.color.white};
        }
    }
    ${LanguageSelected} {
        color: ${Variables.color.white};
    }
`

const Header = ({ noticeBar = false, headerBackground }) => {
    const { SidebarOpen, setSidebarOpen, isLanguageMenuOpen, setLanguageMenuOpen } = useContext(LayoutContext)
    const [shouldShowActions, setShouldShowActions] = useState(false)
    const [lastYPos, setLastYPos] = useState(0)
    const [isMenuOpen, setMenuOpen] = useState(false)
    const data = useHeaderData()
    const BrochureData = data.allContentfulHeader.edges[0].node.brochure
    useEffect(() => {
        function handleScroll() {
            const yPos = window.scrollY
            const isScrollingUp = yPos < lastYPos

            setShouldShowActions(isScrollingUp)
            setLastYPos(yPos)
        }
        window.addEventListener('scroll', handleScroll, false)

        return () => {
            window.removeEventListener('scroll', handleScroll, false)
        }
    }, [lastYPos])

    const controls = useAnimation()

    useEffect(() => {
        controls.start(isMenuOpen ? 'active' : 'inactive') // Second should be 'inactive'
    }, [isMenuOpen, controls])

    const SidebarNavigationStyles = {
        active: {
            opacity: 1,
        },
        inactive: {
            opacity: 0,
        },
    }

    // scroll container
    const scrollRef = useRef(null)

    // page scrollable height based on content length
    const [pageHeight, setPageHeight] = useState(0)

    // update scrollable height when browser is resizing
    const resizePageHeight = useCallback((entries) => {
        for (let entry of entries) {
            setPageHeight(entry.contentRect.height)
        }
    }, [])

    // observe when browser is resizing
    useLayoutEffect(() => {
        const resizeObserver = new ResizeObserver((entries) => resizePageHeight(entries))
        scrollRef && resizeObserver.observe(scrollRef.current)
        return () => resizeObserver.disconnect()
    }, [scrollRef, resizePageHeight])

    const topOffset = useCurrentWidth() < 1024 ? 0 : 0

    const { scrollY } = useViewportScroll()
    const stickyHeaderHeight = shouldShowActions || isMenuOpen ? 0 : -pageHeight

    const rangeFrom = (isMenuOpen || SidebarOpen || isLanguageMenuOpen) && noticeBar ? -topOffset : 0
    const rangeTo =
        (isMenuOpen || SidebarOpen || isLanguageMenuOpen) && noticeBar
            ? -topOffset
            : stickyHeaderHeight - (noticeBar ? topOffset : 0)

    const transform = useTransform(scrollY, [0, pageHeight], [rangeFrom, rangeTo])

    const physics = { damping: 13, mass: 0.27, stiffness: 75 }
    const spring = useSpring(transform, physics)

    return (
        <>
            <StickyHeader
                ref={scrollRef}
                headerBackground={headerBackground}
                style={{ y: spring }}
                isLanguageMenuOpen={isLanguageMenuOpen}
                SidebarOpen={SidebarOpen}
                isStuck={shouldShowActions && scrollY.current > 0}
                isMenuOpen={isMenuOpen}
                className={isMenuOpen ? 'menuisOpen' : ''}
            >
                <StyledMobileView>
                    <TopBar between="xs">
                        <SelectLanguage />
                        {BrochureData && (
                            <BrochureButton>
                                <Button buttonLink={{ externalLink: BrochureData.file.url }} tinted>
                                    <FormattedMessage id={'Brochure'} />
                                </Button>
                            </BrochureButton>
                        )}
                    </TopBar>
                </StyledMobileView>
                <HeaderWrapper>
                    <Container>
                        <LogoWrap>
                            <LocalLink to="/">
                                <Logo />
                            </LocalLink>
                        </LogoWrap>
                        <DesktopMenu>
                            <Navigation data={data} />
                            <SelectLanguage />
                            {BrochureData && (
                                <BrochureButton>
                                    <Button buttonLink={{ externalLink: BrochureData.file.url }} tinted>
                                        <FormattedMessage id={'Brochure'} />
                                    </Button>
                                </BrochureButton>
                            )}
                        </DesktopMenu>
                        <MenuToggle
                            onClick={() => {
                                isMenuOpen ? setMenuOpen(false) : setMenuOpen(true)
                                setSidebarOpen(false)
                                setLanguageMenuOpen(false)
                            }}
                            isMenuOpen={isMenuOpen}
                        ></MenuToggle>
                        <MobileClose
                            onClick={() => {
                                isMenuOpen ? setMenuOpen(false) : setMenuOpen(true)
                                setSidebarOpen(false)
                                setLanguageMenuOpen(false)
                            }}
                            isMenuOpen={isMenuOpen}
                        >
                            <Close />
                        </MobileClose>
                    </Container>
                </HeaderWrapper>
            </StickyHeader>
            <SidebarNavigation
                animate={controls}
                variants={SidebarNavigationStyles}
                transition={{ type: 'tween' }}
                isActive={isMenuOpen}
            >
                <SidebarNavigationInner>
                    <MobileNavigation>
                        <Navigation data={data} />
                    </MobileNavigation>
                </SidebarNavigationInner>
            </SidebarNavigation>
        </>
    )
}

export default Header
